import { useRef, useState } from "react";
import { Camera, ImageUp, Loader2, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useSeo } from "@/lib/seo";

export default function VisualSearch() {
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [results, setResults] = useState([]);
  const [aiMode, setAiMode] = useState("live");
  const inputRef = useRef(null);

  useSeo({
    title: "Recherche par image — ISMO-TECH",
    description: "Trouvez un logiciel ou un produit ISMO-TECH à partir d'une simple photo ou capture d'écran.",
  });

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formats acceptés : JPEG, PNG ou WEBP");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setLoading(true);
    setDone(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/search/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setKeywords(data.keywords || "");
      setResults(data.results || []);
      setAiMode(data.ai_mode || "live");
      setDone(true);
    } catch (e) {
      toast.error(apiError(e, "Analyse de l'image impossible"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(""); setResults([]); setKeywords(""); setDone(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="container-it py-8 lg:py-12" data-testid="visual-search-page">
      <div className="mx-auto max-w-2xl text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand">
          <Camera className="h-7 w-7" />
        </span>
        <h1 className="mt-4 font-display text-3xl font-extrabold text-ink sm:text-4xl">Recherche par image</h1>
        <p className="mt-2 text-sm text-slate-500">
          Envoyez une photo ou une capture d'écran d'un logiciel : notre IA identifie le produit et vous
          propose les articles correspondants du catalogue ISMO-TECH.
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-2xl">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid="visual-search-file"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {!preview ? (
          <button
            type="button"
            data-testid="visual-search-dropzone"
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-line bg-white px-6 py-12 text-center transition-colors duration-200 hover:border-brand hover:bg-brand-soft/40"
          >
            <ImageUp className="h-10 w-10 text-brand" />
            <span className="font-display text-base font-bold text-ink">Choisir une image</span>
            <span className="text-xs text-slate-500">JPEG, PNG ou WEBP — jusqu'à 8 Mo</span>
          </button>
        ) : (
          <div className="relative overflow-hidden rounded-3xl border border-line bg-white p-4">
            <button
              type="button" onClick={reset} data-testid="visual-search-reset"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow hover:text-ink"
            >
              <X className="h-4 w-4" />
            </button>
            <img src={preview} alt="Aperçu" className="mx-auto max-h-72 rounded-2xl object-contain" />
            {loading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-brand">
                <Loader2 className="h-4 w-4 animate-spin" /> Analyse de l'image en cours...
              </div>
            )}
          </div>
        )}
      </div>

      {done && (
        <div className="mx-auto mt-8 max-w-6xl" data-testid="visual-search-results">
          {keywords && (
            <div className="mb-5 flex flex-wrap items-center justify-center gap-2 text-sm">
              <Sparkles className="h-4 w-4 text-brand" />
              <span className="text-slate-500">Détecté :</span>
              <span className="rounded-full bg-brand-soft px-3 py-1 font-bold text-brand">{keywords}</span>
            </div>
          )}
          {aiMode === "demo" && (
            <p className="mb-4 text-center text-xs text-amber-600">
              (Analyse visuelle IA indisponible — voici nos produits les plus populaires.)
            </p>
          )}
          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4">
              {results.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-line bg-white py-12 text-center">
              <Search className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 font-display text-base font-bold text-ink">Aucun produit correspondant</p>
              <p className="mt-1 text-sm text-slate-500">Essayez une autre image plus nette ou centrée sur le produit.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
